import {
    Redeem,
    Info,
    Event,
    DateRange,
    Description,
    AttachMoney,
    AccountBalanceWallet,
    Category,
    CreditCard,
    Email,
    Phone,
    Person,
    Percent,
    ShoppingBag,
    Celebration
} from '@mui/icons-material';
import { Alert, alpha, Box, Button, Card, CardContent, CardMedia, Chip, CircularProgress, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Slide, Typography, useTheme } from '@mui/material'
import { formatCardNumber, formatDate, getStatusColor } from '../../../helpers/cardHelpers';
import config from '../../../Config/config.json'

export const RewardDetails = ({ reward, handleRedeem, loading }:
    { reward: any, handleRedeem: () => void, loading: boolean }) => {

    const theme = useTheme();

    return (
        <Slide in={!!reward} direction="up">
            <Box>
                {reward && (
                    <Paper
                        elevation={3}
                        sx={{
                            mt: 3,
                            p: { xs: 2, md: 4 },
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.background.paper, 0.95),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                        }}
                    >
                        {/* Status Badge */}
                        <Chip
                            label={reward.status?.toUpperCase()}
                            color={getStatusColor(reward.status)}
                            size="medium"
                            icon={<Celebration />}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                fontWeight: 'bold',
                                px: 1,
                                fontSize: '0.85rem'
                            }}
                        />

                        {/* Header */}
                        <Box sx={{ mb: 3, pr: 8 }}>
                            <Typography variant="h5" fontWeight="700" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <Celebration color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                                Reward Details
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                Voucher Code: <strong>{reward.voucherCode}</strong>
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        <Grid container spacing={4}>
                            {/* Reward Products Section */}
                            {reward.rewardMeta?.products && reward.rewardMeta.products.length > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <ShoppingBag color="primary" sx={{ mr: 1.5 }} />
                                        Reward Products ({reward.rewardMeta.totalProducts || reward.rewardMeta.products.length})
                                    </Typography>
                                    <Grid container spacing={3}>
                                        {reward.rewardMeta.products.map((product: any, index: number) => (
                                            <Grid item xs={6} sm={4} md={3} key={product.productId || index}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 1.5,
                                                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: alpha(theme.palette.primary.main, 0.3),
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                            transform: 'translateY(-2px)'
                                                        },
                                                        position: 'relative'
                                                    }}
                                                >
                                                    {/* Discount Badge */}
                                                    {product.discountPercentage > 0 && (
                                                        <Chip
                                                            label={`${product.discountPercentage}% OFF`}
                                                            color="success"
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                left: 8,
                                                                zIndex: 1,
                                                                fontSize: '0.65rem',
                                                                height: 22,
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    )}

                                                    {/* Product Image */}
                                                    <Box sx={{
                                                        height: 140,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                        p: 1,
                                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                                                    }}>
                                                        {product.image ? (
                                                            <CardMedia
                                                                component="img"
                                                                sx={{
                                                                    height: '100%',
                                                                    width: 'auto',
                                                                    maxWidth: '100%',
                                                                    objectFit: 'contain'
                                                                }}
                                                                image={config.imgUrl + product.image}
                                                                alt={product.name}
                                                            />
                                                        ) : (
                                                            <ShoppingBag sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.5 }} />
                                                        )}
                                                    </Box>

                                                    <CardContent sx={{
                                                        flexGrow: 1,
                                                        p: 1.5,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        '&:last-child': { pb: 1.5 }
                                                    }}>
                                                        {/* Product Name - Truncated */}
                                                        <Typography
                                                            variant="subtitle2"
                                                            fontWeight="600"
                                                            sx={{
                                                                lineHeight: 1.3,
                                                                height: 32,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical'
                                                            }}
                                                        >
                                                            {product.name}
                                                        </Typography>

                                                        {/* Price Row */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
                                                            {product.discountPrice != null ? (
                                                                <>
                                                                    {product.price > 0 && product.discountPrice !== 0 && (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                textDecoration: 'line-through',
                                                                                color: 'text.disabled',
                                                                                fontSize: '0.8rem'
                                                                            }}
                                                                        >
                                                                            ₹{product.price}
                                                                        </Typography>
                                                                    )}
                                                                    <Typography
                                                                        variant="h6"
                                                                        color={product.discountPrice === 0 ? 'primary.main' : 'success.main'}
                                                                        fontWeight="700"
                                                                        sx={{ fontSize: '1rem' }}
                                                                    >
                                                                        {product.discountPrice === 0 ? 'FREE' : `₹${product.discountPrice}`}
                                                                    </Typography>
                                                                </>
                                                            ) : (
                                                                <Typography variant="h6" fontWeight="700" sx={{ fontSize: '1rem' }}>
                                                                    {product.price === 0 ? 'FREE' : `₹${product.price}`}
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        {/* Category Badge */}
                                                        {product.category && (
                                                            <Chip
                                                                label={product.category}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    height: 20,
                                                                    mt: 0.5,
                                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                                    color: theme.palette.primary.main,
                                                                    alignSelf: 'flex-start'
                                                                }}
                                                            />
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}

                            {/* Customer Details Section */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Person color="primary" sx={{ mr: 1.5 }} />
                                    Customer Information
                                </Typography>

                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Person color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Name</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{reward.customer?.customerName || 'N/A'}</Typography>}
                                        />
                                    </ListItem>

                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Phone color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Mobile</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{reward.customer?.mobileNumber || 'N/A'}</Typography>}
                                        />
                                    </ListItem>

                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Email color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Email</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{reward.customer?.emailAddress || 'N/A'}</Typography>}
                                        />
                                    </ListItem>

                                    {reward.customer?.city && (
                                        <ListItem sx={{ px: 0, py: 1.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <Info color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" color="text.secondary">Location</Typography>}
                                                secondary={
                                                    <Typography variant="body1" fontWeight="500">
                                                        {reward.customer.city}, {reward.customer.state}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>

                            {/* Card Details Section */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CreditCard color="primary" sx={{ mr: 1.5 }} />
                                    Card Details
                                </Typography>

                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CreditCard color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Card Number</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{formatCardNumber(reward.cardNumber)}</Typography>}
                                        />
                                    </ListItem>

                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Category color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Card Type</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{reward.cardType || 'N/A'}</Typography>}
                                        />
                                    </ListItem>

                                    {reward.card?.balance !== undefined && (
                                        <ListItem sx={{ px: 0, py: 1.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <AccountBalanceWallet color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" color="text.secondary">Card Balance</Typography>}
                                                secondary={<Typography variant="body1" fontWeight="500" color="primary.main">₹{reward.card.balance}</Typography>}
                                            />
                                        </ListItem>
                                    )}

                                    {reward.card?.points !== undefined && (
                                        <ListItem sx={{ px: 0, py: 1.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <AttachMoney color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" color="text.secondary">Points</Typography>}
                                                secondary={<Typography variant="body1" fontWeight="500" color="secondary.main">{reward.card.points}</Typography>}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>

                            {/* Rule Information Section */}
                            {reward.rule && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Description color="primary" sx={{ mr: 1.5 }} />
                                        Rule Information
                                    </Typography>

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                                        }}
                                    >
                                        <Typography variant="h6" color="primary.main" fontWeight="700" gutterBottom>
                                            {reward.rule.name}
                                        </Typography>

                                        <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                            {reward.rule.description}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                                            {reward.rule.triggerType && (
                                                <Chip
                                                    label={`Trigger: ${reward.rule.triggerType}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            )}
                                            {reward.rule.triggerValue && (
                                                <Chip
                                                    label={`Value: ${reward.rule.triggerValue}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                />
                                            )}
                                            {reward.rule.perCardLimit && (
                                                <Chip
                                                    label={`Limit: ${reward.rule.perCardLimit}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                />
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            )}

                            {/* Dates Section */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <DateRange color="primary" sx={{ mr: 1.5 }} />
                                    Timeline
                                </Typography>

                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0, py: 1.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Event color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="subtitle2" color="text.secondary">Created At</Typography>}
                                            secondary={<Typography variant="body1" fontWeight="500">{formatDate(reward.createdAt)}</Typography>}
                                        />
                                    </ListItem>

                                    {reward.claimedAt && (
                                        <ListItem sx={{ px: 0, py: 1.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <Redeem color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" color="text.secondary">Claimed At</Typography>}
                                                secondary={<Typography variant="body1" fontWeight="500">{formatDate(reward.claimedAt)}</Typography>}
                                            />
                                        </ListItem>
                                    )}

                                    {reward.dates?.expiryDate && (
                                        <ListItem sx={{ px: 0, py: 1.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <Info color="warning" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Typography variant="subtitle2" color="text.secondary">Expiry Date</Typography>}
                                                secondary={<Typography variant="body1" fontWeight="500" color="warning.main">{formatDate(reward.dates.expiryDate)}</Typography>}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>
                        </Grid>

                        {/* Redeem Button */}
                        {reward.status?.toLowerCase() === 'claimed' && (
                            <Box sx={{ textAlign: 'center', mt: 6 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleRedeem}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Redeem />}
                                    sx={{
                                        py: 1.75,
                                        px: 6,
                                        borderRadius: 3,
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                    size="large"
                                >
                                    {loading ? 'Processing...' : 'Redeem Now'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Click to redeem the reward and complete the process
                                </Typography>
                            </Box>
                        )}

                        {reward.status?.toLowerCase() === 'redeemed' && (
                            <Alert
                                severity="info"
                                sx={{
                                    borderRadius: 2,
                                    mt: 4,
                                    border: `1px solid ${theme.palette.info.main}`,
                                    bgcolor: alpha(theme.palette.info.main, 0.05)
                                }}
                                icon={<Redeem />}
                            >
                                <Typography fontWeight="600">This reward has already been redeemed</Typography>
                                {reward.claimedAt && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Redeemed on {formatDate(reward.claimedAt)}
                                    </Typography>
                                )}
                            </Alert>
                        )}
                    </Paper>
                )}
            </Box>
        </Slide>
    );
}
